import React, { useMemo } from 'react';
import { ImageModelGradient } from './ImageModelGradient';
import { AudioModelGradient } from './AudioModelGradient';
import { BusinessContentGradient } from './BusinessContentGradient';
import { ModelGradientProps } from './ModelGradient.types';
import { TextModelGradient } from './TextModelGradient';

const DEFAULT_COLORS = ['#E0EBE6', '#5CD699', '#99D65C'];

export function ModelGradient({
    gradient,
    colors,
    scale: _scale,
    rotate: _rotate,
    animate = 'none',
    borderRadius = '12px',
}: ModelGradientProps) {
    const Gradient =
        {
            image: ImageModelGradient,
            voice_over: AudioModelGradient,
            music: AudioModelGradient,
            business_content: BusinessContentGradient,
            text: TextModelGradient,
        }[gradient] ?? ImageModelGradient; // TODO: remove this once we have a gradient for each model

    const updatedColors = useMemo(() => (colors.length !== 3 ? DEFAULT_COLORS : colors), [colors]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Gradient colors={updatedColors} scale="none" rotate={0} animate={animate} borderRadius={borderRadius} />
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0.15,
                    mixBlendMode: 'overlay',
                    pointerEvents: 'none',
                }}
            >
                <NoiseOverlay animate={animate} />
            </div>
        </div>
    );
}

function NoiseOverlay({ animate }: { animate: 'none' | 'hover' | 'always' }) {
    return (
        <svg
            className="noise-overlay"
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 0 700 700"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
        >
            <defs>
                <linearGradient id="grayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#000000" />
                    <stop offset="25%" stopColor="#222222" />
                    <stop offset="50%" stopColor="#444444" />
                    <stop offset="75%" stopColor="#666666" />
                    <stop offset="100%" stopColor="#888888" />
                </linearGradient>
                <filter
                    id="nnnoise-filter"
                    x="-20%"
                    y="-20%"
                    width="140%"
                    height="140%"
                    filterUnits="objectBoundingBox"
                    primitiveUnits="userSpaceOnUse"
                    colorInterpolationFilters="linearRGB"
                >
                    <feTurbulence
                        type="fractalNoise"
                        baseFrequency="1"
                        numOctaves="4"
                        seed="5"
                        stitchTiles="stitch"
                        x="0%"
                        y="0%"
                        width="100%"
                        height="100%"
                        result="turbulence"
                    >
                        {animate === 'always' && (
                            <animate
                                attributeName="baseFrequency"
                                dur="30s"
                                values="1.25;1.35;1.25"
                                repeatCount="indefinite"
                            />
                        )}
                    </feTurbulence>
                    <feSpecularLighting
                        surfaceScale="5"
                        specularConstant="3"
                        specularExponent="20"
                        lightingColor="#ffffff"
                        x="0%"
                        y="0%"
                        width="100%"
                        height="100%"
                        in="turbulence"
                        result="specularLighting"
                    >
                        <feDistantLight azimuth="3" elevation="120" />
                    </feSpecularLighting>
                </filter>
            </defs>
            <rect width="700" height="700" fill="url(#grayGradient)" filter="url(#nnnoise-filter)" />
        </svg>
    );
}
